// Object-Oriented Models for Flights Tracker refactoring

export class Leg {
  constructor({
    id = '',
    itineraryId = '',
    flightNumber = '', // Flight number digits (e.g. 1729)
    airlineCode = 'UA', // Airline carrier code (e.g. UA)
    date = '',
    // Cooldown & Status tracking
    lastRefreshed = null,
    lastSyncError = null,
    flightyUrl = '',
    departureAirport = '',
    // API-returned schema fields:
    flight_date = null,
    flight_status = 'scheduled',
    departure = null,
    arrival = null,
    airline = null,
    flight = null,
    aircraft = null,
    live = null
  } = {}) {
    this.id = id || `leg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.itineraryId = itineraryId;
    this.flightNumber = (flightNumber || '').trim().toUpperCase();
    this.airlineCode = (airlineCode || 'UA').trim().toUpperCase();
    this.date = date; // User inputted date, e.g. "2027-01-07" or trip day format
 
    this.lastRefreshed = lastRefreshed;
    this.lastSyncError = lastSyncError || null;
    this.flightyUrl = flightyUrl;
    this.departureAirport = (departureAirport || '').trim().toUpperCase();

    // Aviation Stack API response payload fields (populated on sync)
    this.flight_date = flight_date || date;
    this.flight_status = flight_status || 'scheduled';
    
    this.departure = departure || {
      airport: '',
      timezone: '',
      iata: '',
      icao: '',
      terminal: '',
      gate: '',
      delay: 0,
      scheduled: '',
      estimated: '',
      actual: '',
      estimated_runway: '',
      actual_runway: '',
      baggage: ''
    };

    this.arrival = arrival || {
      airport: '',
      timezone: '',
      iata: '',
      icao: '',
      terminal: '',
      gate: '',
      delay: 0,
      scheduled: '',
      estimated: '',
      actual: '',
      estimated_runway: '',
      actual_runway: '',
      baggage: ''
    };

    this.airline = airline || {
      id: '',
      fleet_average_age: 0,
      airline_id: '',
      callsign: '',
      hub_code: '',
      iata_code: '',
      icao_code: '',
      country_iso2: '',
      date_founded: '',
      iata_prefix_accounting: '',
      airline_name: '',
      country_name: '',
      fleet_size: 0,
      status: '',
      type: ''
    };

    this.flight = flight || {
      number: '',
      iata: '',
      icao: '',
      codeshared: {}
    };

    this.aircraft = aircraft || {
      registration: '',
      iata: '',
      icao: '',
      icao24: ''
    };

    this.live = live || null;
  }

  // Serializes for Firestore database writes
  toFirestore() {
    return {
      id: this.id,
      itineraryId: this.itineraryId,
      flightNumber: this.flightNumber,
      airlineCode: this.airlineCode,
      date: this.date,
      lastRefreshed: this.lastRefreshed,
      lastSyncError: this.lastSyncError,
      flightyUrl: this.flightyUrl,
      departureAirport: this.departureAirport,
      flight_date: this.flight_date,
      flight_status: this.flight_status,
      departure: this.departure,
      arrival: this.arrival,
      airline: this.airline,
      flight: this.flight,
      aircraft: this.aircraft,
      live: this.live
    };
  }

  // Updates API fields after querying Aviation Stack
  updateFromApiResponse(apiData) {
    if (!apiData) return;
    this.flight_date = apiData.flight_date || this.flight_date;
    this.flight_status = apiData.flight_status || this.flight_status;
    this.departure = { ...this.departure, ...apiData.departure };
    this.arrival = { ...this.arrival, ...apiData.arrival };
    this.airline = { ...this.airline, ...apiData.airline };
    this.flight = { ...this.flight, ...apiData.flight };
    this.aircraft = { ...this.aircraft, ...apiData.aircraft };
    this.live = apiData.live ? { ...this.live, ...apiData.live } : null;
    this.lastRefreshed = Date.now();
  }

  // Updates API fields from flightapi.io /airline array payload, e.g.
  // [{ departure: { airport, airportCode, scheduledTime: "14:55, Jul 16", estimatedTime,
  //                 departureDateTime, offGroundTime, terminal, gate } },
  //  { arrival:   { airport, airportCode, scheduledTime, estimatedTime, arrivalDateTime,
  //                 onGroundTime, inGateTime, timeRemaining, terminal, gate, baggage } }]
  updateFromFlightApi(apiData) {
    if (!apiData || !Array.isArray(apiData)) return;

    const pick = (key) => {
      const obj = apiData.find(item => item && item[key]);
      if (!obj) return null;
      return Array.isArray(obj[key]) ? obj[key][0] : obj[key];
    };

    const splitTermGate = (data) => {
      let term = data.terminal || "";
      let gate = data.gate || "";
      const combined = data["Terminal - Gate"] || "";
      if (!term && !gate && combined) {
        const parts = combined.split('-').map(s => s.trim());
        if (parts.length > 1) [term, gate] = parts;
        else if (/gate/i.test(combined)) gate = combined;
        else term = combined;
      }
      return { term: term || "", gate: gate || "" };
    };

    const dep = pick('departure');
    const arr = pick('arrival');

    if (dep) {
      const { term, gate } = splitTermGate(dep);
      this.departure = {
        ...this.departure,
        airport: dep.airport || dep.Airport || this.departure.airport,
        iata: dep.airportCode || this.departure.iata,
        scheduled: dep.scheduledTime || dep["Scheduled Time"] || this.departure.scheduled,
        estimated: dep.estimatedTime || dep["Estimated Time"] || this.departure.estimated || "",
        actual: dep.offGroundTime || dep.outGateTime || "",
        terminal: term || this.departure.terminal,
        gate: gate || this.departure.gate,
      };
    }

    if (arr) {
      const { term, gate } = splitTermGate(arr);
      this.arrival = {
        ...this.arrival,
        airport: arr.airport || arr.Airport || this.arrival.airport,
        iata: arr.airportCode || this.arrival.iata,
        scheduled: arr.scheduledTime || arr["Scheduled Time"] || this.arrival.scheduled,
        estimated: arr.estimatedTime || arr["Estimated Time"] || this.arrival.estimated || "",
        actual: arr.onGroundTime || arr.inGateTime || "",
        terminal: term || this.arrival.terminal,
        gate: gate || this.arrival.gate,
        baggage: arr.baggage || ""
      };
    }

    // The API has no single status field; an explicit one is used if present,
    // otherwise status is derived from the gate/runway timestamps.
    const statusObj = apiData.find(item => item && typeof item.status === 'string');
    if (statusObj) {
      this.flight_status = statusObj.status.toLowerCase();
    } else if (arr && (arr.onGroundTime || arr.inGateTime)) {
      this.flight_status = 'landed';
    } else if (dep && dep.offGroundTime) {
      this.flight_status = 'active';
    } else {
      this.flight_status = 'scheduled';
    }

    const toMin = (t) => {
      const m = (t || '').match(/(\d{1,2}):(\d{2})/);
      return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
    };
    const s = toMin(this.departure.scheduled);
    const e = toMin(this.departure.estimated);
    this.departure.delay = (s !== null && e !== null && e > s) ? e - s : 0;

    this.lastRefreshed = Date.now();
    this.lastSyncError = null;
  }
}

export class Itinerary {
  constructor({
    id = '',
    passengers = [], // Must be an array of passenger names (strings)
    direction = 'incoming', // incoming or outgoing
    legs = []
  } = {}) {
    this.id = id || `itinerary_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.passengers = Array.isArray(passengers) ? passengers : [passengers].filter(Boolean);
    this.direction = direction; // 'incoming' or 'outgoing'
    this.legs = legs.map(l => l instanceof Leg ? l : new Leg(l));
  }

  // Serializes for Firestore database (Itineraries contain ONLY passengers and direction)
  toFirestore() {
    return {
      id: this.id,
      passengers: this.passengers,
      direction: this.direction
    };
  }

  // Resolves the primary/first date of travel
  getTravelDate() {
    if (this.legs.length === 0) return '';
    return this.legs[0].date;
  }

  // Computes dynamic timeline status from child legs
  getTimelineStatus(isDemoMode = false, isTestModeToday = false) {
    if (this.legs.length === 0) {
      return { text: "No Legs", class: "status-scheduled" };
    }

    // Evaluate live/API statuses first
    let anyInFlight = false;
    let allLanded = true;
    let anyDelayed = false;

    this.legs.forEach(leg => {
      const status = (leg.flight_status || '').toLowerCase();
      if (status === 'active' || status === 'in-flight' || status === 'en-route' || status === 'in air' || status === 'started') {
        anyInFlight = true;
      }
      if (status !== 'landed') {
        allLanded = false;
      }
      if (leg.departure && leg.departure.delay > 0) {
        anyDelayed = true;
      }
    });

    if (anyInFlight) return { text: "In-Flight", class: "status-inflight" };
    if (allLanded) return { text: "Landed", class: "status-landed" };
    if (anyDelayed) return { text: "Delayed", class: "status-delayed" };

    return { text: "Scheduled", class: "status-scheduled" };
  }
}
